/**
 * useAwards — points summary, award levels and earn rules (GET /UserProfile/Awards).
 */
import { useCallback, useEffect, useState } from 'react';
import profileService from '../api/services/profileService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type {
  AwardLevelItem,
  AwardsSummary,
  EarnRuleItem,
} from '../types/userProfile';

export interface UseAwardsResult {
  summary: AwardsSummary | null;
  levels: AwardLevelItem[];
  earnRules: EarnRuleItem[];
  loading: boolean;
  error: string;
  loaded: boolean;
  refresh: () => void;
}

export const useAwards = (): UseAwardsResult => {
  const [summary, setSummary] = useState<AwardsSummary | null>(null);
  const [levels, setLevels] = useState<AwardLevelItem[]>([]);
  const [earnRules, setEarnRules] = useState<EarnRuleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await profileService.getAwards();
      const data = response?.data;
      if (data?.success === false) {
        setError(data?.message || '');
        return;
      }
      setSummary(data?.summary ?? null);
      setLevels(Array.isArray(data?.levels) ? data.levels : []);
      setEarnRules(Array.isArray(data?.earnRules) ? data.earnRules : []);
    } catch (caught) {
      setError(getApiErrorMessage(caught as ApiError));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load, reloadToken]);

  const refresh = useCallback(() => setReloadToken(token => token + 1), []);

  return { summary, levels, earnRules, loading, error, loaded, refresh };
};

export default useAwards;
